/*
  Taking the money, when the agent is the one taking it.

  Two halves of one moment on a phone call: "here is where to send it", then "it arrived, here is
  what I can see about it".

  The account details come from the API, which reads the same table the app would have shown. An
  agent working off a number in their head is how a customer pays into an account we closed last
  year — so every value here is copyable in one click, because retyping a MoMo number from a
  screen into a chat is where a digit gets lost, and the digit that gets lost is in an account
  number.

  What is recorded is a real Transaction, not a flag. Money that exists only as a boolean never
  appears in revenue, never reconciles against a statement, and cannot be found when a customer
  says they paid.
*/
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Typography,
  Upload
} from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import moment from 'moment'
import {
  getRequest,
  postRequest,
  insuranceRailsUrl,
  insurancePolicyPaymentUrl,
  errorNotification,
  successNotification
} from '../../config'

const { Option } = Select
const { Text } = Typography

const serverSaid = response =>
  response.error?.response?.data?.detail || response.data?.detail

const money = (amount, currency) =>
  amount === null || amount === undefined ? '—' : `${currency || 'GHS'} ${amount}`

const PolicyPayment = ({ policy, onChanged }) => {
  const [rails, setRails] = useState([])
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [receipt, setReceipt] = useState(null)
  const [form] = Form.useForm()

  useEffect(() => {
    const load = async () => {
      const response = await getRequest(
        insuranceRailsUrl(policy.country || 'GH'),
        'payment details',
        () => {}
      )
      if (response.success) setRails(response.data.rails || [])
    }
    if (!policy.isPaid) load()
    // eslint-disable-next-line
  }, [policy.id, policy.isPaid])

  const record = async values => {
    setBusy(true)
    // Multipart whenever a receipt is attached, JSON otherwise — the endpoint takes both, and a
    // FormData with no file is a needlessly awkward thing to debug.
    let payload
    let asJson = true
    if (receipt) {
      asJson = false
      payload = new FormData()
      payload.append('receipt', receipt)
      payload.append('amount', String(values.amount))
      payload.append('rail', values.rail || '')
      payload.append('payerName', values.payerName || '')
      payload.append('payerNumber', values.payerNumber || '')
      payload.append('payerReference', values.payerReference || '')
      payload.append('paidAt', values.paidAt ? values.paidAt.format('YYYY-MM-DD') : '')
      payload.append('note', values.note || '')
    } else {
      payload = {
        amount: String(values.amount),
        rail: values.rail || '',
        payerName: values.payerName || '',
        payerNumber: values.payerNumber || '',
        payerReference: values.payerReference || '',
        paidAt: values.paidAt ? values.paidAt.format('YYYY-MM-DD') : '',
        note: values.note || ''
      }
    }
    const response = await postRequest(
      insurancePolicyPaymentUrl(policy.id),
      payload,
      'payment',
      () => {},
      asJson
    )
    setBusy(false)
    if (!response.success) {
      errorNotification(serverSaid(response) || 'That payment could not be recorded.')
      return
    }
    successNotification('Payment recorded.')
    setAsking(false)
    setReceipt(null)
    form.resetFields()
    onChanged(response.data.policy)
  }

  const confirmAndRecord = values => {
    Modal.confirm({
      title: 'Record this payment?',
      content: (
        <span>
          Marks the policy paid and puts <strong>{money(values.amount, policy.currency)}</strong>{' '}
          in the books. Only do this once the money is actually with us.
        </span>
      ),
      okText: 'Yes, it arrived',
      onOk: () => record(values)
    })
  }

  if (policy.isPaid) return <Recorded policy={policy} />

  return (
    <Card size="small" title="Take payment">
      {rails.length === 0 ? (
        <Empty
          description={
            <Space direction="vertical">
              <span>No payment rails are configured for insurance.</span>
              <span style={{ opacity: 0.65 }}>
                Setup → Payments decides which rails insurance offers and holds the account
                details for each.
              </span>
            </Space>
          }
        />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {rails.map(rail => (
            <RailDetails key={rail.rail} rail={rail} />
          ))}
        </Space>
      )}

      <Button
        type="primary"
        style={{ marginTop: 16 }}
        onClick={() => {
          form.setFieldsValue({
            amount: policy.totalAmount,
            paidAt: moment(),
            rail: rails[0]?.rail
          })
          setAsking(true)
        }}
      >
        Record a payment
      </Button>

      <Modal
        visible={asking}
        title="Record a payment"
        okText="Record it"
        confirmLoading={busy}
        onCancel={() => {
          setAsking(false)
          setReceipt(null)
        }}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={confirmAndRecord}>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="amount"
                label="Amount that arrived"
                extra="Defaults to the policy total. Change it if less came in."
                rules={[{ required: true, message: 'How much arrived?' }]}
              >
                <InputNumber min={0.01} step={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="rail" label="How it came">
                <Select placeholder="Choose a rail">
                  {rails.map(rail => (
                    <Option key={rail.rail} value={rail.rail}>
                      {rail.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="payerName"
                label="Who sent it"
                extra="As it appears on our statement — not always the policyholder."
              >
                <Input placeholder="Kofi Mensah" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="payerNumber" label="Their number or account">
                <Input placeholder="0244 123 456" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="payerReference"
                label="Their reference"
                extra="From their receipt. The thing to search a statement for."
              >
                <Input placeholder="MM123456" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              {/*
                When the money ARRIVED, which is not when it is being typed. Recording Friday's
                transfers on Monday has to leave the statement and the record agreeing.
              */}
              <Form.Item name="paidAt" label="When it arrived">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="note" label="Note (optional)">
            <Input.TextArea rows={2} placeholder="Anything worth remembering about this payment" />
          </Form.Item>
          <Form.Item label="Their receipt (optional)">
            <Upload
              beforeUpload={selected => {
                setReceipt(selected)
                return false
              }}
              maxCount={1}
              onRemove={() => setReceipt(null)}
            >
              <Button icon={<UploadOutlined />}>Attach a screenshot or PDF</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  )
}

PolicyPayment.propTypes = {
  policy: PropTypes.object.isRequired,
  onChanged: PropTypes.func.isRequired
}

/* Our details for one rail, every value one click from the clipboard. */
const RailDetails = ({ rail }) => (
  <div>
    <div style={{ fontWeight: 600, marginBottom: 4 }}>{rail.label}</div>
    {rail.account ? (
      <Space direction="vertical" size={2}>
        {rail.account.bankName ? <Copyable label="Bank" value={rail.account.bankName} /> : null}
        <Copyable label="Account name" value={rail.account.accountName} />
        <Copyable label="Number" value={rail.account.accountNumber} />
        {rail.account.branch ? <Copyable label="Branch" value={rail.account.branch} /> : null}
        {rail.account.instructions ? (
          <div style={{ opacity: 0.65, fontSize: 12 }}>{rail.account.instructions}</div>
        ) : null}
      </Space>
    ) : (
      /*
        A real answer, not a gap. Showing an empty account number to somebody about to send money
        is worse than saying the rail is unavailable.
      */
      <Alert
        type="warning"
        showIcon
        message="We hold no account details for this rail yet."
        description="Setup → Payments is where they go. Until then there is nothing to read out."
      />
    )}
  </div>
)

RailDetails.propTypes = { rail: PropTypes.object.isRequired }

const Copyable = ({ label, value }) => (
  <div>
    <span style={{ opacity: 0.6, fontSize: 12, marginRight: 8 }}>{label}</span>
    <Text copyable>{value}</Text>
  </div>
)

Copyable.propTypes = { label: PropTypes.string, value: PropTypes.string }

/* What was recorded. "Did this customer pay?" answered on the page about that customer. */
const Recorded = ({ policy }) => {
  const payment = policy.payment
  return (
    <Card size="small" title="Payment">
      {payment ? (
        <Descriptions size="small" column={{ xs: 1, md: 2 }} bordered>
          <Descriptions.Item label="Amount">
            {money(payment.amount, payment.currency)}
          </Descriptions.Item>
          <Descriptions.Item label="How it came">{payment.rail || '—'}</Descriptions.Item>
          <Descriptions.Item label="Who sent it">{payment.payerName || '—'}</Descriptions.Item>
          <Descriptions.Item label="Their number">{payment.payerNumber || '—'}</Descriptions.Item>
          <Descriptions.Item label="Their reference">
            {payment.payerReference || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="When it arrived">
            {payment.paidAt ? moment(payment.paidAt).format('D MMM YYYY, HH:mm') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Our reference">{payment.reference || '—'}</Descriptions.Item>
          <Descriptions.Item label="Recorded by">{payment.recordedBy || '—'}</Descriptions.Item>
          {payment.note ? (
            <Descriptions.Item label="Note" span={2}>
              {payment.note}
            </Descriptions.Item>
          ) : null}
          {payment.receiptUrl ? (
            <Descriptions.Item label="Receipt" span={2}>
              <a href={payment.receiptUrl} target="_blank" rel="noopener noreferrer">
                Open the receipt
              </a>
            </Descriptions.Item>
          ) : null}
        </Descriptions>
      ) : (
        /*
          Paid, with nothing recorded here — a customer who paid through the app's own rails
          before this desk existed. Said plainly rather than shown as an empty table.
        */
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Amount">
            {money(policy.totalAmount, policy.currency)}
          </Descriptions.Item>
          <Descriptions.Item label="Reference">
            {policy.transactionReference || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="How">
            Paid through the app, not recorded by hand.
          </Descriptions.Item>
        </Descriptions>
      )}
    </Card>
  )
}

Recorded.propTypes = { policy: PropTypes.object.isRequired }

export default PolicyPayment
